/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/VirtualMeetingModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import Select from "react-select";
import styles from "./index.module.css";

const VirtualMeetingModal = ({
  dialogRef = null,
  open,
  onClose,
  onConfirm,
  isLoading = false,
  selectedAppointment = null,
  eventData = null,
}) => {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  // Generate weekdays from day after event ends until account deactivation date (Monday-Friday only)
  const generateWeekdays = () => {
    const weekdays = [];

    // If no event data, return empty array
    if (
      !eventData ||
      !eventData.fechaFinEvento ||
      !eventData.fechaVigenciaCompradores
    ) {
      return weekdays;
    }

    // Parse fechaFinEvento (format: 2025-11-10) and add 1 day
    // Parse date string manually to avoid timezone issues
    const fechaFinEventoParts = eventData.fechaFinEvento.split("-");
    const fechaFinEvento = new Date(
      parseInt(fechaFinEventoParts[0]),
      parseInt(fechaFinEventoParts[1]) - 1,
      parseInt(fechaFinEventoParts[2]),
    );
    fechaFinEvento.setDate(fechaFinEvento.getDate() + 1);

    // Parse fechaVigenciaCompradores (format: 2025-12-11T23:59:59.000Z)
    // Extract date part to avoid timezone issues
    const fechaVigenciaDate = new Date(eventData.fechaVigenciaCompradores);
    const fechaVigenciaCompradores = new Date(
      fechaVigenciaDate.getFullYear(),
      fechaVigenciaDate.getMonth(),
      fechaVigenciaDate.getDate(),
    );

    // Start from the day after event ends
    const currentDate = new Date(fechaFinEvento);

    // Set time to start of day for proper comparison
    currentDate.setHours(0, 0, 0, 0);

    // Get current language for proper date formatting
    const currentLanguage = i18n.language || "en";
    const localeMap = {
      en: "en-US",
      es: "es-ES",
      fr: "fr-FR",
    };
    const locale = localeMap[currentLanguage] || "en-US";

    // Iterate from day after event ends until account deactivation date
    while (currentDate <= fechaVigenciaCompradores) {
      const dayOfWeek = currentDate.getDay();

      // Only include weekdays (Monday = 1, Friday = 5)
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const dateString = currentDate.toISOString().split("T")[0];

        // Format date based on language
        let displayDate;
        if (currentLanguage === "es") {
          // Spanish format: Lunes 27 de Octubre de 2025
          displayDate = currentDate.toLocaleDateString(locale, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          });
        } else if (currentLanguage === "fr") {
          // French format: Lundi 27 octobre 2025
          displayDate = currentDate.toLocaleDateString(locale, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          });
        } else {
          // English format: Monday, October 27, 2025
          displayDate = currentDate.toLocaleDateString(locale, {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          });
        }

        weekdays.push({
          value: dateString,
          label: displayDate,
          date: new Date(currentDate),
        });
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return weekdays;
  };

  // Generate time slots from 7 AM to 5 PM (1 hour intervals)
  const generateTimeSlots = () => {
    const timeSlots = [];

    for (let hour = 7; hour <= 17; hour++) {
      const timeString =
        hour < 12
          ? `${hour}:00 AM`
          : hour === 12
            ? "12:00 PM"
            : `${hour - 12}:00 PM`;

      timeSlots.push({
        value: hour,
        label: timeString,
        hour: hour,
      });
    }

    return timeSlots;
  };

  const weekdays = generateWeekdays();
  const timeSlots = generateTimeSlots();

  useEffect(() => {
    if (dialogRef && dialogRef.current) {
      const dialog = dialogRef.current;
      if (open) {
        dialog.showModal();
        // Reset selections when modal opens
        setSelectedDay(null);
        setSelectedTime(null);
      } else {
        dialog.close();
      }
    }
  }, [open, dialogRef]);

  useEffect(() => {
    // Cleanup: close dialog when component unmounts
    return () => {
      if (dialogRef && dialogRef.current) {
        dialogRef.current.close();
      }
    };
  }, [dialogRef]);

  const handleConfirm = async () => {
    if (!selectedDay || !selectedTime) {
      return; // Don't proceed if day or time not selected
    }

    if (onConfirm) {
      // Create the virtual meeting data
      // Parse the date string and create a date in local timezone
      const [year, month, day] = selectedDay.value.split("-").map(Number);
      const selectedDate = new Date(
        year,
        month - 1,
        day,
        selectedTime.value,
        0,
        0,
        0,
      );

      const endDate = new Date(selectedDate);
      endDate.setHours(selectedDate.getHours() + 1);

      const companyName =
        selectedAppointment?.memberName ||
        selectedAppointment?.buyerCompanyName ||
        t("common.unknownCompany");
      const virtualMeetingData = {
        subject: t("components.chat.virtualMeeting.subject", {
          company: companyName,
        }),
        startDateTime: selectedDate.toISOString(),
        endDateTime: endDate.toISOString(),
        attendeeEmail: selectedAppointment?.userData?.correoElectronico,
        organizerUserId: selectedAppointment?.idUsuarioComprador,
        attendeeUserId: selectedAppointment?.idUsuarioRemitente,
      };

      await onConfirm(virtualMeetingData);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isConfirmDisabled = !selectedDay || !selectedTime || isLoading;

  return (
    <>
      {open && <div className={styles.modalOverlay}></div>}
      <dialog ref={dialogRef} className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {t("pages.viewAppointments.virtualMeetingModalTitle")}
          </h3>
          <button
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.modalSubtitle}>
            {t("pages.viewAppointments.virtualMeetingModalSubtitle")}
          </p>

          <div className={styles.formRow}>
            <div className={styles.formColumn}>
              <label className={styles.formLabel}>
                {t("pages.viewAppointments.virtualMeetingDayLabel")}
              </label>
              <Select
                options={weekdays}
                value={selectedDay}
                onChange={setSelectedDay}
                placeholder={t(
                  "pages.viewAppointments.virtualMeetingDayPlaceholder",
                )}
                isDisabled={isLoading}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    height: "40px",
                    fontSize: "16px",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    borderColor: baseStyles.borderColor,
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    zIndex: 10002,
                  }),
                }}
              />
            </div>

            <div className={styles.formColumn}>
              <label className={styles.formLabel}>
                {t("pages.viewAppointments.virtualMeetingTimeLabel")}
              </label>
              <Select
                options={timeSlots}
                value={selectedTime}
                onChange={setSelectedTime}
                placeholder={t(
                  "pages.viewAppointments.virtualMeetingTimePlaceholder",
                )}
                isDisabled={isLoading}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    height: "40px",
                    fontSize: "16px",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    borderColor: baseStyles.borderColor,
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    zIndex: 10002,
                  }),
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.modalActions}>
          <button
            className={`${styles.modalButton} ${styles.cancelButton}`}
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </button>
          <button
            className={`${styles.modalButton} ${styles.confirmButton}`}
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {isLoading ? (
              <span className={styles.loadingText}>
                {t("pages.viewAppointments.creatingVirtualMeeting")}
              </span>
            ) : (
              t("pages.viewAppointments.createVirtualMeeting")
            )}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default VirtualMeetingModal;
